import { useState, useEffect, useCallback } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { useNavigate } from "react-router-dom";

interface ServiceType {
  id: number;
  name: string;
  description: string;
  base_rate: number;
  unit: string;
  is_active: number;
  created_at?: string;
}

interface ServiceFormData {
  name: string;
  description: string;
  base_rate: string;
  unit: string;
  is_active: boolean;
}

const UNIT_OPTIONS = [
  "per acre", "per km", "per location", "lump sum", "per day",
  "per hour", "per sqm", "per plot", "per point", "per sheet",
];

const DEFAULT_FORM: ServiceFormData = {
  name: "",
  description: "",
  base_rate: "",
  unit: "lump sum",
  is_active: true,
};

function ServiceFormModal({
  service,
  onSave,
  onClose,
  saving,
}: {
  service: ServiceType | null;
  onSave: (data: ServiceFormData) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<ServiceFormData>(
    service
      ? {
          name: service.name,
          description: service.description || "",
          base_rate: String(service.base_rate),
          unit: service.unit,
          is_active: service.is_active === 1,
        }
      : DEFAULT_FORM
  );

  const set = (field: keyof ServiceFormData, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <i className="ri-tools-line text-xl text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{service ? "Edit Service" : "Add New Service"}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {service ? `Editing: ${service.name}` : "Create a new service type for quotations"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer transition-all"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Service Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              placeholder="e.g. Topographic Survey"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={2}
              placeholder="Brief description of this service..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none transition-all"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Base Rate (₹) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₹</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.base_rate}
                  onChange={(e) => set("base_rate", e.target.value)}
                  required
                  placeholder="0"
                  className="w-full pl-7 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Unit</label>
              <select
                value={form.unit}
                onChange={(e) => set("unit", e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 bg-white transition-all"
              >
                {UNIT_OPTIONS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <button
              type="button"
              onClick={() => set("is_active", !form.is_active)}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                form.is_active ? "bg-amber-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                  form.is_active ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
            <div>
              <p className="text-sm font-medium text-gray-700">
                {form.is_active ? "Active" : "Inactive"}
              </p>
              <p className="text-xs text-gray-400">
                {form.is_active
                  ? "Visible in quotation service picker"
                  : "Hidden from quotation service picker"}
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim() || !form.base_rate}
              className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-600 transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin text-sm" />Saving...</>
              ) : (
                <><i className="ri-save-line text-sm" />{service ? "Update Service" : "Add Service"}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  service,
  onConfirm,
  onCancel,
}: {
  service: ServiceType;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <i className="ri-delete-bin-line text-xl text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Delete Service</h3>
            <p className="text-xs text-gray-500 mt-0.5">{service.name}</p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Are you sure you want to delete <strong>{service.name}</strong>? This cannot be undone and may affect existing quotations.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-red-600 transition-all whitespace-nowrap"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ServicesManagementPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
  const [editTarget, setEditTarget] = useState<ServiceType | null | "new">(null);
  const [deleteTarget, setDeleteTarget] = useState<ServiceType | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const getToken = () => localStorage.getItem("token");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch("/api/services", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      const list: ServiceType[] = Array.isArray(data) ? data : (data.data || []);
      setServices(list);
    } catch {
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleSave = async (form: ServiceFormData) => {
    setSaving(true);
    try {
      const token = getToken();
      const isEdit = editTarget && editTarget !== "new";
      const url = isEdit ? `/api/services/${(editTarget as ServiceType).id}` : "/api/services";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          base_rate: parseFloat(form.base_rate) || 0,
          unit: form.unit,
          is_active: form.is_active ? 1 : 0,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed" }));
        throw new Error(err.error || "Failed to save service");
      }

      showToast(isEdit ? `${form.name} updated` : `${form.name} added`);
      setEditTarget(null);
      await fetchServices();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (service: ServiceType) => {
    try {
      const token = getToken();
      await fetch(`/api/services/${service.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: service.name,
          description: service.description,
          base_rate: service.base_rate,
          unit: service.unit,
          is_active: service.is_active === 1 ? 0 : 1,
        }),
      });
      setServices((prev) =>
        prev.map((s) =>
          s.id === service.id ? { ...s, is_active: s.is_active === 1 ? 0 : 1 } : s
        )
      );
      showToast(`${service.name} ${service.is_active === 1 ? "deactivated" : "activated"}`);
    } catch {
      showToast("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = getToken();
      await fetch(`/api/services/${deleteTarget.id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setServices((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      showToast(`${deleteTarget.name} deleted`);
    } catch {
      showToast("Failed to delete service");
    } finally {
      setDeleteTarget(null);
    }
  };

  const filtered = services.filter((s) => {
    const matchSearch =
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.description || "").toLowerCase().includes(search.toLowerCase());
    const matchActive =
      filterActive === "all" ||
      (filterActive === "active" && s.is_active === 1) ||
      (filterActive === "inactive" && s.is_active === 0);
    return matchSearch && matchActive;
  });

  const activeCount = services.filter((s) => s.is_active === 1).length;
  const inactiveCount = services.filter((s) => s.is_active === 0).length;

  return (
    <ERPLayout title="Services Management">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl font-medium">
          <i className="ri-checkbox-circle-line text-amber-400" />
          {toast}
        </div>
      )}

      {editTarget !== null && (
        <ServiceFormModal
          service={editTarget === "new" ? null : editTarget}
          onSave={handleSave}
          onClose={() => setEditTarget(null)}
          saving={saving}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          service={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 cursor-pointer transition-all"
            >
              <i className="ri-arrow-left-line text-sm" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Services Management</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Manage service types used in quotations and job cards
              </p>
            </div>
          </div>
          <button
            onClick={() => setEditTarget("new")}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-all"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </span>
            Add Service
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Total Services", value: services.length, icon: "ri-tools-line", color: "#f59e0b", bg: "#fffbeb" },
            { label: "Active", value: activeCount, icon: "ri-checkbox-circle-line", color: "#10b981", bg: "#ecfdf5" },
            { label: "Inactive", value: inactiveCount, icon: "ri-close-circle-line", color: "#6b7280", bg: "#f3f4f6" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.bg }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-sm" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search services by name or description..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            {(["all", "active", "inactive"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilterActive(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
                  filterActive === f
                    ? "bg-amber-500 text-white"
                    : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Services Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Service Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Description</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Base Rate</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Unit</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-16 text-gray-400">
                        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-xl">
                          <i className="ri-tools-line text-2xl text-gray-300" />
                        </div>
                        <p className="text-sm font-medium">No services found</p>
                        <p className="text-xs mt-1">
                          {search ? "Try adjusting your search" : "Add your first service type"}
                        </p>
                      </td>
                    </tr>
                  )}
                  {filtered.map((service) => (
                    <tr
                      key={service.id}
                      className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-tools-line text-amber-600 text-sm" />
                          </div>
                          <p className="text-sm font-semibold text-gray-800">{service.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        <p className="text-sm text-gray-500 max-w-xs truncate">
                          {service.description || <span className="text-gray-300 italic">No description</span>}
                        </p>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-sm font-bold text-gray-900">
                          ₹{Number(service.base_rate).toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                          {service.unit}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <button
                          onClick={() => handleToggleActive(service)}
                          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
                            service.is_active === 1 ? "bg-amber-500" : "bg-gray-300"
                          }`}
                          title={service.is_active === 1 ? "Click to deactivate" : "Click to activate"}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                              service.is_active === 1 ? "translate-x-5" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setEditTarget(service)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-all"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(service)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-all"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && (
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing <strong>{filtered.length}</strong> of <strong>{services.length}</strong> services
              </p>
              <p className="text-xs text-gray-400">
                Changes apply immediately to new quotations
              </p>
            </div>
          )}
        </div>
      </div>
    </ERPLayout>
  );
}